import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';

import { dashboardRefusal } from '../../services/dashboards';
import { DASHBOARD_DESCRIPTION_MAX, DASHBOARD_NAME_MAX } from '../../utils/dashboards';

/** "Stock (copy)", held to the name's limit. */
export function copyDashboardName(name) {
	const base = typeof name === 'string' && name.trim() ? name.trim() : 'Dashboard';
	const suffix = ' (copy)';
	return `${base.slice(0, DASHBOARD_NAME_MAX - suffix.length)}${suffix}`;
}

const TITLES = { create: 'New dashboard', rename: 'Rename dashboard', duplicate: 'Duplicate dashboard' };
const OK_TEXT = { create: 'Create', rename: 'Save', duplicate: 'Duplicate' };
const FALLBACKS = {
	create: 'Could not create the dashboard.',
	rename: 'Could not rename the dashboard.',
	duplicate: 'Could not duplicate the dashboard.',
};

/**
 * The window that names a dashboard, in NameChartModal's shape: **New
 * dashboard** (a name for a new, personal one), **Rename dashboard** (its
 * name and description) or **Duplicate dashboard** (a personal copy under a
 * new name). The name is required, at most 100 characters, unique among the
 * dashboards it will sit with; a clash, a full tier or any other refusal is
 * said in the window, where the person is still typing (the server's own
 * sentence).
 *
 *   mode       'create' | 'rename' | 'duplicate'
 *   dashboard  the dashboard named (none for create)
 *   onSubmit   ({ name, description }) => Promise — rejects with the refusal
 */
export default function NameDashboardModal({ open, mode = 'rename', dashboard, onSubmit, onClose }) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const duplicating = mode === 'duplicate';
	const creating = mode === 'create';

	useEffect(() => {
		if (!open) return;
		let seeded = dashboard?.name || '';
		if (duplicating) seeded = copyDashboardName(dashboard?.name);
		if (creating) seeded = '';
		setName(seeded);
		setDescription(creating ? '' : (dashboard?.description || ''));
		setError(null);
		setSaving(false);
	}, [open, duplicating, creating, dashboard]);

	const submit = async () => {
		const trimmed = name.trim();
		if (!trimmed) {
			setError('Give the dashboard a name.');
			return;
		}
		setSaving(true);
		try {
			await onSubmit({ name: trimmed, description: description.trim() });
			onClose();
		} catch (e) {
			setError(dashboardRefusal(e, FALLBACKS[mode] || FALLBACKS.rename));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			title={TITLES[mode] || TITLES.rename}
			okText={OK_TEXT[mode] || OK_TEXT.rename}
			onOk={submit}
			confirmLoading={saving}
			onCancel={onClose}
			destroyOnClose
			className='name-chart-modal name-dashboard-modal'
		>
			{duplicating || creating ? (
				<p className='name-chart-modal__hint'>{duplicating ? 'The copy is yours: only you see it.' : 'It is yours to start with: only you see it until a manager pins it for everyone.'}</p>
			) : null}
			<label className='name-chart-modal__label' htmlFor='dashboard-name'>Name</label>
			<Input
				id='dashboard-name'
				autoFocus
				maxLength={DASHBOARD_NAME_MAX}
				value={name}
				onChange={(event) => { setName(event.target.value); setError(null); }}
				onPressEnter={submit}
			/>
			<label className='name-chart-modal__label' htmlFor='dashboard-description'>Description</label>
			<Input.TextArea
				id='dashboard-description'
				maxLength={DASHBOARD_DESCRIPTION_MAX}
				autoSize={{ minRows: 2, maxRows: 5 }}
				value={description}
				placeholder='What this dashboard is for, in a sentence'
				onChange={(event) => { setDescription(event.target.value); setError(null); }}
			/>
			{error ? <div className='name-chart-modal__error' role='alert'>{error}</div> : null}
		</Modal>
	);
}
